import React, { useEffect, useState, useCallback } from "react";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { specLabel } from "@/lib/specialisms";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, HelpCircle, Trash2, Send, User, Instagram, Music2, Linkedin, Film } from "lucide-react";

const STATUS_META = {
    pending: { label: "Pending", cls: "bg-yc-cream" },
    more_info: { label: "More info", cls: "bg-yc-pink text-white" },
    approved: { label: "Accepted", cls: "bg-yc-blue text-white" },
    rejected: { label: "Rejected", cls: "bg-yc-ink text-white" },
};

const Vetting = () => {
    const [queue, setQueue] = useState([]);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [decision, setDecision] = useState({
        notes: "", quality_score: 75, reliability_score: 75,
        checklist: { quality: false, professionalism: false, brand_safety: false, reliability: false },
    });

    const load = useCallback(async (keepActiveId) => {
        const r = await api.get("/admin/vetting-queue");
        setQueue(r.data);
        const keep = keepActiveId && r.data.find((c) => c.creator_id === keepActiveId);
        setActive(keep || r.data[0] || null);
    }, []);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!active) { setMessages([]); return; }
        api.get(`/admin/creator/${active.creator_id}/messages`).then((r) => setMessages(r.data));
    }, [active?.creator_id]);

    const toggle = (k) => setDecision({ ...decision, checklist: { ...decision.checklist, [k]: !decision.checklist[k] } });

    const setStatus = async (status) => {
        if (!active) return;
        try {
            await api.post(`/admin/creator/${active.creator_id}/status`, {
                status,
                notes: decision.notes,
                quality_score: decision.quality_score,
                reliability_score: decision.reliability_score,
            });
            toast.success(`Status set to ${STATUS_META[status].label}`);
            await load(status === "pending" || status === "more_info" ? active.creator_id : undefined);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not update status");
        }
    };

    const sendMessage = async () => {
        if (!active || !draft.trim()) return;
        setSending(true);
        try {
            const r = await api.post(`/admin/creator/${active.creator_id}/message`, { body: draft.trim() });
            setMessages((m) => [...m, r.data]);
            setDraft("");
            toast.success("Message sent — the creator has been emailed");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not send message");
        } finally { setSending(false); }
    };

    const deleteCreator = async () => {
        if (!active) return;
        if (!window.confirm(`Permanently remove ${active.name || "this creator"} from the platform? This deletes their account, profile and messages. This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/creator/${active.creator_id}`);
            toast.success("Creator removed from the platform");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not delete creator");
        }
    };

    const personal = active ? [
        { l: "First name", v: active.first_name },
        { l: "Surname", v: active.surname },
        { l: "Age", v: active.age },
        { l: "Telephone", v: active.telephone },
        { l: "Email", v: active.contact_email },
        { l: "Address", v: active.address },
        { l: "Account name", v: active.bank_account_name },
        { l: "Sort code", v: active.bank_sort_code },
        { l: "Account number", v: active.bank_account_number },
        { l: "Agreement", v: active.agreement_accepted ? `Accepted ${active.agreement_version || ""} · ${active.agreement_accepted_at ? new Date(active.agreement_accepted_at).toLocaleDateString() : ""}` : "NOT accepted" },
    ] : [];

    return (
        <DashboardShell role="admin" title="Vetting queue" subtitle="Review, message, approve or remove creators">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="yc-card-sm p-4 lg:col-span-1">
                    <h3 className="font-heading text-lg font-bold">In queue ({queue.length})</h3>
                    <div className="mt-4 space-y-2">
                        {queue.map((c) => (
                            <button key={c.creator_id} onClick={() => setActive(c)} data-testid={`queue-${c.creator_id}`}
                                className={`w-full text-left p-3 rounded-xl border-2 border-yc-ink transition-all ${active?.creator_id === c.creator_id ? "bg-yc-blue text-white" : "bg-white"}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <div className="font-semibold truncate">{c.name || "Unnamed"}</div>
                                    <span className={`yc-badge text-[10px] shrink-0 ${active?.creator_id === c.creator_id ? "bg-white text-yc-ink" : STATUS_META[c.vetting_status]?.cls || "bg-yc-cream"}`}>
                                        {STATUS_META[c.vetting_status]?.label || c.vetting_status}
                                    </span>
                                </div>
                                <div className="text-xs opacity-75 truncate">{(c.specialisms || []).map(specLabel).join(", ")}</div>
                            </button>
                        ))}
                        {queue.length === 0 && <div className="text-center text-yc-ink/60 py-6 text-sm">Queue is empty.</div>}
                    </div>
                </div>

                {active && (
                    <div className="lg:col-span-2 space-y-6">
                        <div className="yc-card p-6 space-y-4">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="yc-overline">Creator</div>
                                    <h2 className="font-heading text-3xl font-bold mt-1" data-testid="vet-name">{active.name || "Unnamed"}</h2>
                                    <div className="text-sm text-yc-ink/70 mt-1">{active.location} · {active.availability}</div>
                                </div>
                                <span className={`yc-badge ${STATUS_META[active.vetting_status]?.cls || "bg-yc-cream"}`} data-testid="vet-status-badge">
                                    {STATUS_META[active.vetting_status]?.label || active.vetting_status}
                                </span>
                            </div>
                            <p className="text-sm leading-relaxed">{active.bio}</p>

                            {/* Personal details — admin only */}
                            <div className="border-2 border-yc-ink rounded-2xl p-4 bg-yc-cream" data-testid="vet-personal-details">
                                <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-yc-pink" />
                                    <div className="yc-overline text-yc-pink">Personal details (admin only)</div>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3 text-sm">
                                    {personal.map((p) => (
                                        <div key={p.l}>
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-yc-ink/50">{p.l}</div>
                                            <div className="font-semibold break-words">{p.v || "—"}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Socials */}
                            <div>
                                <div className="yc-overline">Social handles</div>
                                <div className="flex flex-wrap gap-2 mt-2 text-sm">
                                    {[
                                        { icon: Instagram, v: active.instagram_handle, url: `https://instagram.com/${active.instagram_handle}` },
                                        { icon: Music2, v: active.tiktok_handle, url: `https://tiktok.com/@${active.tiktok_handle}` },
                                        { icon: Linkedin, v: active.linkedin_handle || active.youtube_handle, url: active.linkedin_handle ? `https://linkedin.com/in/${active.linkedin_handle}` : `https://youtube.com/@${active.youtube_handle}` },
                                    ].map((s, i) => {
                                        const Icon = s.icon;
                                        return s.v ? (
                                            <a key={`soc-${i}`} href={s.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 border-2 border-yc-ink rounded-full px-3 py-1 bg-white hover:bg-yc-ink hover:text-white transition-colors">
                                                <Icon className="w-3.5 h-3.5" /> @{s.v}
                                            </a>
                                        ) : (
                                            <span key={`soc-${i}`} className="flex items-center gap-2 border-2 border-dashed border-yc-ink/30 rounded-full px-3 py-1 text-yc-ink/40">
                                                <Icon className="w-3.5 h-3.5" /> not provided
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Work links */}
                            <div data-testid="vet-work-links">
                                <div className="yc-overline flex items-center gap-1"><Film className="w-3.5 h-3.5" /> Work</div>
                                <div className="flex flex-col gap-1 mt-2 text-sm">
                                    {active.social_links && <div><span className="text-yc-ink/60">Social: </span><span className="break-all">{active.social_links}</span></div>}
                                    {active.portfolio_link && <div><span className="text-yc-ink/60">Portfolio: </span><a href={active.portfolio_link} target="_blank" rel="noreferrer" className="text-yc-blue underline break-all">{active.portfolio_link}</a></div>}
                                    {active.showreel_link && <div><span className="text-yc-ink/60">Showreel: </span><a href={active.showreel_link} target="_blank" rel="noreferrer" className="text-yc-blue underline break-all">{active.showreel_link}</a></div>}
                                    {(active.instagram_video_links || []).map((link, i) => (
                                        <a key={`igv-${i}`} href={link} target="_blank" rel="noreferrer" className="text-yc-blue underline break-all">{link}</a>
                                    ))}
                                    {!active.social_links && !active.portfolio_link && !active.showreel_link && (!active.instagram_video_links || active.instagram_video_links.length === 0) && <span className="text-yc-ink/50">No work links provided.</span>}
                                </div>
                            </div>

                            <div>
                                <div className="yc-overline">Specialisms</div>
                                <div className="flex flex-wrap gap-2 mt-2">{(active.specialisms || []).map((s) => <span key={s} className="yc-badge bg-white">{specLabel(s)}</span>)}</div>
                            </div>
                            <div>
                                <div className="yc-overline">Portfolio</div>
                                <div className="grid grid-cols-3 gap-2 mt-2">
                                    {(active.portfolio || []).map((p, i) => (
                                        <div key={`pf-${i}`} className="border-2 border-yc-ink rounded-xl p-3 bg-yc-cream text-xs">
                                            <div className="yc-badge bg-white">{p.kind}</div>
                                            <div className="mt-1 truncate">{p.title}</div>
                                        </div>
                                    ))}
                                    {(!active.portfolio || active.portfolio.length === 0) && <div className="text-yc-ink/60 text-sm">No portfolio items.</div>}
                                </div>
                            </div>
                            <div>
                                <div className="yc-overline">Audience</div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {(active.audience_stats || []).map((a, i) => (
                                        <div key={a.platform || `aud-${i}`} className="border-2 border-yc-ink rounded-full px-3 py-1 text-xs bg-white">{a.platform}: {a.follower_count?.toLocaleString()}</div>
                                    ))}
                                </div>
                            </div>

                            <div className="border-t-2 border-yc-ink pt-4">
                                <div className="yc-overline">Vetting checklist</div>
                                <div className="grid grid-cols-2 gap-2 mt-3">
                                    {Object.keys(decision.checklist).map((k) => (
                                        <label key={k} className="flex items-center gap-2 text-sm">
                                            <input type="checkbox" data-testid={`vet-check-${k}`} checked={decision.checklist[k]} onChange={() => toggle(k)} className="w-5 h-5" />
                                            <span className="capitalize">{k.replace("_", " ")}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="yc-overline">Quality score</label>
                                    <input data-testid="vet-quality" type="number" min="0" max="100" className="yc-input mt-1" value={decision.quality_score} onChange={(e) => setDecision({ ...decision, quality_score: parseFloat(e.target.value || 0) })} />
                                </div>
                                <div>
                                    <label className="yc-overline">Reliability score</label>
                                    <input data-testid="vet-reliability" type="number" min="0" max="100" className="yc-input mt-1" value={decision.reliability_score} onChange={(e) => setDecision({ ...decision, reliability_score: parseFloat(e.target.value || 0) })} />
                                </div>
                            </div>
                            <div>
                                <label className="yc-overline">Notes</label>
                                <textarea data-testid="vet-notes" rows={3} className="yc-input mt-1" value={decision.notes} onChange={(e) => setDecision({ ...decision, notes: e.target.value })} />
                            </div>

                            <div className="flex flex-wrap gap-3 items-center">
                                <button data-testid="vet-approve" onClick={() => setStatus("approved")} className="yc-btn-primary"><CheckCircle2 className="w-4 h-4 mr-2" /> Accept</button>
                                <button data-testid="vet-more-info" onClick={() => setStatus("more_info")} className="yc-btn-accent"><HelpCircle className="w-4 h-4 mr-2" /> More info</button>
                                <button data-testid="vet-pending" onClick={() => setStatus("pending")} className="yc-btn-secondary"><Clock className="w-4 h-4 mr-2" /> Pending</button>
                                <button data-testid="vet-reject" onClick={() => setStatus("rejected")} className="yc-btn-secondary"><XCircle className="w-4 h-4 mr-2" /> Reject</button>
                                <button data-testid="vet-delete" onClick={deleteCreator} className="ml-auto flex items-center gap-2 text-sm font-bold text-yc-pink border-2 border-yc-pink rounded-full px-4 py-2 hover:bg-yc-pink hover:text-white transition-colors">
                                    <Trash2 className="w-4 h-4" /> Delete from platform
                                </button>
                            </div>
                        </div>

                        {/* Message thread */}
                        <div className="yc-card p-6" data-testid="vet-messages-card">
                            <h3 className="font-heading text-xl font-bold">Messages</h3>
                            <p className="text-xs text-yc-ink/60 mt-1">Ask the creator for more information — they get an email and can reply from their dashboard.</p>
                            <div className="mt-4 space-y-3 max-h-72 overflow-y-auto pr-1">
                                {messages.map((m) => (
                                    <div key={m.message_id} className={`max-w-[85%] ${m.sender === "admin" ? "ml-auto" : ""}`}>
                                        <div className={`border-2 border-yc-ink rounded-2xl px-4 py-2.5 text-sm ${m.sender === "admin" ? "bg-yc-blue text-white" : "bg-yc-cream"}`}>
                                            {m.body}
                                        </div>
                                        <div className={`text-[10px] text-yc-ink/50 mt-1 ${m.sender === "admin" ? "text-right" : ""}`}>
                                            {m.sender === "admin" ? "You" : active.name || "Creator"} · {new Date(m.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                ))}
                                {messages.length === 0 && <div className="text-center text-yc-ink/50 text-sm py-4">No messages yet.</div>}
                            </div>
                            <div className="flex gap-2 mt-4">
                                <input
                                    data-testid="vet-message-input"
                                    className="yc-input flex-1"
                                    placeholder="e.g. Please add a link to recent work…"
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") sendMessage(); }}
                                />
                                <button data-testid="vet-message-send" disabled={sending || !draft.trim()} onClick={sendMessage} className="yc-btn-primary px-4 disabled:opacity-50">
                                    <Send className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardShell>
    );
};

export default Vetting;
